import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import { getCompanyById } from '@/services/companyService'
import type { User, LoginCredentials, RegisterData } from '@/types/auth'
import type { Company } from '@/types/company'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)
  const company = ref<Company | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  async function login(credentials: LoginCredentials) {
    loading.value = true
    try {
      console.log('Auth store: Starting login...')
      const response = await authService.login(credentials)
      token.value = response.access_token
      user.value = response.user
      localStorage.setItem('token', response.access_token)
      console.log('Auth store: Login successful, token and user set:', { 
        hasToken: !!token.value, 
        hasUser: !!user.value,
        isAuth: isAuthenticated.value 
      })
      
      // Fetch company info if user has company_id
      if (response.user.company_id) {
        try {
          company.value = await getCompanyById(response.user.company_id)
          console.log('Auth store: Company info loaded:', company.value?.name)
        } catch (error) {
          console.error('Auth store: Failed to fetch company:', error)
        }
      }
      
      return response
    } catch (error) {
      console.error('Auth store: Login failed:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterData) {
    loading.value = true
    try {
      const response = await authService.register(data)
      return response
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    token.value = null
    user.value = null
    company.value = null
    localStorage.removeItem('token')
  }

  async function fetchUser() {
    if (!token.value) return
    
    try {
      const userData = await authService.getProfile()
      user.value = userData
      
      // Fetch company info if user has company_id
      if (userData.company_id) {
        try {
          company.value = await getCompanyById(userData.company_id)
        } catch (error) {
          console.error('Failed to fetch company:', error)
        }
      }
    } catch (error) {
      logout()
    }
  }

  return {
    user,
    token,
    loading,
    company,
    isAuthenticated,
    login,
    register,
    logout,
    fetchUser
  }
})